import { useEffect, useState } from "react";
import {
  adminGetPackets, adminUpsertPacket, adminDeletePacket, formatApiErrorDetail,
} from "@/lib/api";
import {
  Plus, Trash2, Save, X, ChevronUp, ChevronDown, Eye, EyeOff, Radio,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", excerpt: "", body: "", order: 99, published: true, broadcast: false };

function toForm(p) {
  return {
    ...p,
    body: Array.isArray(p.body) ? p.body.join("\n") : (p.body || ""),
    published: !!p.published_at,
    broadcast: false,
  };
}
function toPayload(form) {
  return {
    ...form,
    body: String(form.body || "").split("\n").map((s) => s.trim()).filter(Boolean),
  };
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function AdminPacket() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems(await adminGetPackets()); }
    catch { toast.error("Could not load packet issues."); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY, order: items.length + 1 });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      await adminUpsertPacket(toPayload(editing));
      toast.success(editing.broadcast && editing.published
        ? "Saved. Broadcast triggered to the packet audience."
        : "Saved. The archive updates immediately.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const togglePublished = async (p) => {
    try {
      await adminUpsertPacket({
        id: p.id, slug: p.slug, title: p.title, excerpt: p.excerpt,
        body: Array.isArray(p.body) ? p.body : [],
        order: p.order, published: !p.published_at, broadcast: false,
      });
      toast.success(p.published_at ? "Moved to draft." : "Published to the archive.");
      load();
    } catch { toast.error("Could not update."); }
  };

  const reorder = async (p, dir) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === p.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx], b = sorted[swapIdx];
    const pack = (x, order) => ({
      id: x.id, slug: x.slug, title: x.title, excerpt: x.excerpt,
      body: Array.isArray(x.body) ? x.body : [], order, published: !!x.published_at, broadcast: false,
    });
    try {
      await adminUpsertPacket(pack(a, b.order));
      await adminUpsertPacket(pack(b, a.order));
      load();
    } catch { toast.error("Could not reorder."); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await adminDeletePacket(p.id); toast.success("Deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  const sorted = [...items].sort((a, b) => a.order - b.order);

  return (
    <div data-testid="admin-packet">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Operating Room</p>
          <h1 className="cv-display text-3xl mt-4">The archive, fully editable.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-lg">
            Each issue is a record. Publish, reorder, edit, or unpublish. Tick "broadcast" when publishing to trigger the ActiveCampaign send to the packet audience.
          </p>
        </div>
        <button data-testid="packet-new-btn" onClick={startNew}
          className="cv-btn-primary inline-flex items-center gap-2 self-start whitespace-nowrap">
          <Plus size={15} /> New issue
        </button>
      </div>

      {editing && (
        <PacketForm value={editing} onChange={setEditing} onSave={save} onCancel={cancel} busy={busy} />
      )}

      <div className="mt-8 divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
        {sorted.map((p, i) => (
          <div key={p.id} data-testid="admin-packet-row" className="py-4 flex items-center gap-4">
            <div className="flex flex-col">
              <button onClick={() => reorder(p, "up")} disabled={i === 0}
                className="text-[#9A938A] hover:text-[#1F1B16] disabled:opacity-30" aria-label="Move up">
                <ChevronUp size={15} />
              </button>
              <button onClick={() => reorder(p, "down")} disabled={i === sorted.length - 1}
                className="text-[#9A938A] hover:text-[#1F1B16] disabled:opacity-30" aria-label="Move down">
                <ChevronDown size={15} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="cv-h2 text-sm">{p.title}</p>
              <p className="cv-body text-xs text-[#9A938A] mt-0.5 truncate">
                {p.published_at ? fmtDate(p.published_at) : "Draft"}{p.excerpt ? ` · ${p.excerpt}` : ""}
              </p>
            </div>
            <button data-testid="packet-publish-toggle" onClick={() => togglePublished(p)}
              className={`inline-flex items-center gap-1.5 cv-sans uppercase tracking-[0.1em] text-[0.6rem] px-2.5 py-1 rounded-full border transition-colors ${
                p.published_at ? "border-[#515E4F] text-[#515E4F]" : "border-[rgba(28,27,25,0.3)] text-[#9A938A]"
              }`}>
              {p.published_at ? <Eye size={12} /> : <EyeOff size={12} />}
              {p.published_at ? "Published" : "Draft"}
            </button>
            <button onClick={() => setEditing(toForm(p))} className="cv-link text-xs">Edit</button>
            <button onClick={() => remove(p)} className="text-[#A65A43] hover:text-[#8a4836]" aria-label="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {sorted.length === 0 && (
          <p className="cv-body text-sm text-[#9A938A] py-6">No issues yet.</p>
        )}
      </div>
    </div>
  );
}

function PacketForm({ value, onChange, onSave, onCancel, busy }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div data-testid="admin-packet-form" className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] overflow-hidden">
      <div className="cv-hairline-copper" />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title *">
            <input data-testid="admin-packet-title" className="cv-input"
              value={value.title} onChange={(e) => set("title", e.target.value)} />
          </Field>
          <Field label="Order">
            <input data-testid="admin-packet-order" type="number" className="cv-input"
              value={value.order} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} />
          </Field>
        </div>
        <Field label="Excerpt (the teaser line)">
          <input data-testid="admin-packet-excerpt" className="cv-input"
            value={value.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
        </Field>
        <Field label="Body (one paragraph per line)">
          <textarea data-testid="admin-packet-body" rows={6} className="cv-input resize-none"
            value={value.body} onChange={(e) => set("body", e.target.value)} />
        </Field>

        <div className="flex flex-wrap items-center gap-6 pt-1">
          <button data-testid="admin-packet-published-toggle" type="button"
            onClick={() => set("published", !value.published)} className="inline-flex items-center gap-2.5">
            <span className={`w-9 h-5 rounded-full border transition-colors relative ${value.published ? "bg-[#A6633C] border-[#A6633C]" : "bg-transparent border-[rgba(28,27,25,0.3)]"}`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${value.published ? "left-4 bg-[#F6F1E9]" : "left-0.5 bg-[#9A938A]"}`} />
            </span>
            <span className="cv-sans uppercase tracking-[0.1em] text-[0.68rem] text-[#1F1B16]/70">Published</span>
          </button>
          <button data-testid="admin-packet-broadcast-toggle" type="button"
            onClick={() => set("broadcast", !value.broadcast)} className="inline-flex items-center gap-2.5" title="Trigger the ActiveCampaign broadcast on publish">
            <span className={`w-9 h-5 rounded-full border transition-colors relative ${value.broadcast ? "bg-[#515E4F] border-[#515E4F]" : "bg-transparent border-[rgba(28,27,25,0.3)]"}`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${value.broadcast ? "left-4 bg-[#F6F1E9]" : "left-0.5 bg-[#9A938A]"}`} />
            </span>
            <span className="cv-sans uppercase tracking-[0.1em] text-[0.68rem] text-[#1F1B16]/70 inline-flex items-center gap-1"><Radio size={12} /> Broadcast on publish</span>
          </button>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button data-testid="admin-packet-submit" onClick={onSave} disabled={busy}
            className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {busy ? "Saving..." : "Save issue"}
          </button>
          <button onClick={onCancel} className="cv-btn-secondary inline-flex items-center gap-2">
            <X size={14} /> Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
